/* eslint-disable @typescript-eslint/no-unused-vars */
import Evaporate from 'evaporate';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Image, ProgressBar, Row } from 'react-bootstrap';
import { Control, Controller, FieldError, FieldValues } from 'react-hook-form';
import { BsPersonSquare } from 'react-icons/bs';
import createHash from 'sha.js';
import SparkMD5 from 'spark-md5';
import { api } from '../../../api/core/request';
import { isImageFilename } from '../../../utils/files';
import { Urls } from '../../../utils/urls';
import { FormSubmitContext } from '../../contexts/FormSubmitContext';

enum StatusEnum {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

interface _ProfilePictureUploaderProps extends React.InputHTMLAttributes<HTMLInputElement> {
  dest: string;
  className?: string;
  name: string;
  invalid: boolean;
  isTouched: boolean;
  isClearable: boolean;
  isDirty: boolean;
  error?: FieldError;
  value: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (...event: any[]) => void;
}

function removeQueryStringFromUrl(url: string) {
  const urlObject = new URL(url);
  urlObject.search = '';
  urlObject.hash = '';
  return urlObject.toString();
}

const _ProfilePictureUploader = ({
  dest,
  className,
  onChange,
  onBlur,
  value: externalValue,
  name,
  disabled,
  invalid,
  isTouched,
  isClearable,
  isDirty,
  error: externalError,
  ...props
}: _ProfilePictureUploaderProps) => {
  const destCheckUrl = Urls.s3direct();
  const signerUrl = Urls.s3directSigning();
  const [status, setStatus] = useState(StatusEnum.NOT_STARTED);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | undefined>();
  const [imgSrc, setImgSrc] = useState<string>();
  const [value, setValue] = useState<string>('');
  const [isDisabled, setIsDisabled] = useState(false);
  const [concurrentUploads, setConcurrentUploads] = useState(0);
  const [cancelHandler, setCancelHandler] = useState<() => void>(() => () => {});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const controlledInputRef = useRef<HTMLInputElement>(null);
  const { setIsSubmitEnabled } = useContext(FormSubmitContext);

  useEffect(() => {
    if (concurrentUploads > 0) setIsDisabled(true);
    else setIsDisabled(false);
  }, [concurrentUploads]);

  useEffect(() => {
    if (externalValue) {
      const strippedUrl = removeQueryStringFromUrl(decodeURI(externalValue));
      const filename = decodeURI(strippedUrl.split('/').pop()!);
      setValue(strippedUrl);
      if (
        filename &&
        isImageFilename(filename) &&
        strippedUrl !== externalValue &&
        externalValue.includes('?')
      ) {
        setImgSrc(externalValue);
      }
      setIsSubmitEnabled(true);
      setStatus(StatusEnum.COMPLETED);
    }
  }, [externalValue]);

  useEffect(() => {
    if (externalError) {
      setIsSubmitEnabled(true);
      setStatus(StatusEnum.FAILED);
      setError(externalError.message);
    }
  }, [externalError]);

  const disableSubmit = useCallback(
    (status) => {
      if (status === true) {
        setConcurrentUploads(concurrentUploads + 1);
        return;
      }

      setConcurrentUploads(concurrentUploads - 1);
    },
    [concurrentUploads]
  );

  const finishUpload = (endpoint, bucket, objectKey) => {
    const fileUrl = `${endpoint}/${bucket}/${objectKey}`;
    setValue(fileUrl);
    disableSubmit(false);
    setIsSubmitEnabled(true);
    setStatus(StatusEnum.COMPLETED);
    if (onChange) onChange(fileUrl);
  };

  const computeMd5 = (data) => {
    return btoa(SparkMD5.ArrayBuffer.hash(data, true));
  };

  const computeSha256 = (data) => {
    return createHash('sha256').update(data, 'utf-8').digest('hex');
  };

  const generateAmzInitHeaders = (acl, serverSideEncryption, sessionToken) => {
    const headers = {};
    if (acl) headers['x-amz-acl'] = acl;
    if (sessionToken) headers['x-amz-security-token'] = sessionToken;
    if (serverSideEncryption) {
      headers['x-amz-server-side-encryption'] = serverSideEncryption;
    }
    return headers;
  };

  const generateAmzCommonHeaders = (sessionToken) => {
    const headers = {};
    if (sessionToken) headers['x-amz-security-token'] = sessionToken;
    return headers;
  };

  const generateCustomAuthMethod = (signingUrl, dest) => {
    return async (
      _signParams,
      _signHeaders,
      stringToSign,
      signatureDateTime,
      _canonicalRequest
    ) => {
      const form = new FormData();
      form.append('to_sign', stringToSign);
      form.append('datetime', signatureDateTime);
      form.append('dest', dest);

      try {
        const { data } = await api.post(signingUrl, form, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        });
        return data.s3ObjKey;
      } catch (e) {
        setIsSubmitEnabled(true);
        setStatus(StatusEnum.FAILED);
        return setError(e.message);
      }
    };
  };

  const initiateUpload = (signingUrl, uploadParameters, file, dest) => {
    const createConfig = {
      customAuthMethod: generateCustomAuthMethod(signingUrl, dest),
      aws_key: uploadParameters.access_key_id,
      bucket: uploadParameters.bucket,
      aws_url: uploadParameters.endpoint,
      awsRegion: uploadParameters.region,
      computeContentMd5: true,
      cryptoMd5Method: computeMd5,
      cryptoHexEncodedHash256: computeSha256,
      partSize: 20 * 1024 * 1024,
      logging: true,
      allowS3ExistenceOptimization: uploadParameters.allow_existence_optimization,
      s3FileCacheHoursAgo: uploadParameters.allow_existence_optimization ? 12 : 0,
    };

    const addConfig = {
      name: uploadParameters.object_key,
      file: file,
      contentType: file.type,
      xAmzHeadersCommon: generateAmzCommonHeaders(uploadParameters.session_token),
      xAmzHeadersAtInitiate: generateAmzInitHeaders(
        uploadParameters.acl,
        uploadParameters.server_side_encryption,
        uploadParameters.session_token
      ),
      progress: (progressRatio, _stats) => {
        setProgress(progressRatio);
      },
      warn: (_warnType, _area, msg) => {
        if (msg.includes('InvalidAccessKeyId')) {
          setIsSubmitEnabled(true);
          setStatus(StatusEnum.FAILED);
          setError(msg);
        }
      },
    };

    const optHeaders = {};

    if (uploadParameters.cache_control) {
      optHeaders['Cache-Control'] = uploadParameters.cache_control;
    }

    if (uploadParameters.content_disposition) {
      optHeaders['Content-Disposition'] = uploadParameters.content_disposition;
    }

    addConfig['notSignedHeadersAtInitiate'] = optHeaders;

    Evaporate.create(createConfig).then((evaporate) => {
      disableSubmit(true);
      setIsSubmitEnabled(false);
      setStatus(StatusEnum.IN_PROGRESS);

      setCancelHandler(() => () => {
        evaporate.cancel(`${uploadParameters.bucket}/${addConfig.name}`);
        setValue('');
        setImgSrc(undefined);
        disableSubmit(false);
        if (onChange) onChange(null);
      });

      evaporate.add(addConfig).then(
        (s3Objkey) => {
          setCancelHandler(() => {});
          finishUpload(uploadParameters.endpoint, uploadParameters.bucket, s3Objkey);
        },
        (reason) => {
          setCancelHandler(() => {});
          setIsSubmitEnabled(true);
          setStatus(StatusEnum.FAILED);
          setError(reason);
        }
      );
    });
  };

  const checkFileAndInitiateUpload = async (event) => {
    const file = event.target.files[0];
    const form = new FormData();

    if (file.type.startsWith('image')) {
      const reader = new FileReader();

      reader.onloadend = function () {
        setImgSrc(reader.result?.toString());
      };

      if (file) {
        reader.readAsDataURL(file);
      }
    }

    form.append('dest', dest);
    form.append('name', file.name);
    form.append('type', file.type);
    form.append('size', file.size);
    try {
      const { data: uploadParameters } = await api.post(destCheckUrl, form, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      initiateUpload(signerUrl, uploadParameters, file, dest);
    } catch (e) {
      setIsSubmitEnabled(true);
      setStatus(StatusEnum.FAILED);
      setError(e.message);
    }
  };

  const removeUpload = () => {
    setValue('');
    if (onChange) onChange(null);
    setImgSrc(undefined);
    setIsSubmitEnabled(true);
    setStatus(StatusEnum.NOT_STARTED);
    if (fileInputRef?.current?.value) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`file-uploader-container ${invalid ? 'is-invalid' : ''}`}>
      <div className="file-uploader">
        <Row className="d-flex align-items-center gx-3">
          {!!imgSrc ? (
            <Col xs="auto">
              <Image
                src={imgSrc}
                className="rounded-circle"
                thumbnail
                style={{ width: 96, height: 96 }}
              />
            </Col>
          ) : (
            <Col
              xs="auto"
              className="d-flex align-items-center justify-content-center rounded-circle bg-primary-muted"
              style={{ width: 96, height: 96 }}
            >
              <BsPersonSquare size={32} />
            </Col>
          )}
          <Col xs="auto" className="flex-grow-1">
            {(status === StatusEnum.NOT_STARTED || status === StatusEnum.FAILED) && (
              <Form.Control
                type="file"
                accept="image/png, image/jpg, image/jpeg"
                className={className}
                disabled={isDisabled || disabled}
                onChange={checkFileAndInitiateUpload}
                onBlur={onBlur}
                isInvalid={invalid || !!error}
                ref={fileInputRef}
              />
            )}
            {status === StatusEnum.IN_PROGRESS && (
              <>
                <ProgressBar
                  className="my-3"
                  variant="success"
                  animated
                  now={progress * 100}
                  label={`${Math.floor(progress * 100)}%`}
                />
                {isClearable && (
                  <Col xs="auto">
                    <Button onClick={cancelHandler}>Cancel Upload</Button>
                  </Col>
                )}
              </>
            )}
            {status === StatusEnum.COMPLETED && isClearable && (
              <Button onClick={removeUpload}>Remove File</Button>
            )}
          </Col>
        </Row>
        <input
          type="text"
          ref={controlledInputRef}
          name={name}
          className="d-none"
          value={value}
          onChange={onChange ?? (() => {})}
          {...props}
        />
      </div>
    </div>
  );
};

interface ProfilePictureUploaderProps extends React.InputHTMLAttributes<HTMLInputElement> {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any>;
  isInvalid?: boolean;
  dest: string;
  className?: string;
  isClearable?: boolean;
}

export const ProfilePictureUploader = ({
  name,
  control,
  isInvalid,
  isClearable = true,
  onChange,
  ...props
}: ProfilePictureUploaderProps) => (
  <Controller
    name={name}
    control={control}
    render={({
      field: { onChange: controllerOnChange, onBlur, value, name },
      fieldState: { invalid, isTouched, isDirty, error },
    }) => (
      <>
        <_ProfilePictureUploader
          onChange={(obj) => {
            controllerOnChange(obj);
            if (onChange) onChange(obj);
          }}
          onBlur={onBlur}
          value={value}
          name={name}
          isClearable={isClearable}
          invalid={isInvalid || invalid}
          isTouched={isTouched}
          isDirty={isDirty}
          error={error}
          {...props}
        />
      </>
    )}
  />
);
